import { usePageConfigProviderContext } from "src/features/PageConfigProvider";
import { usePermission, useTranslation } from "src/hooks";
import { FeatureCodes } from "src/utils/constant";
import { useGenGridCol } from "src/utils/HelperComp";
import { TableColumnKey } from "../types/Table";
import useCopyableCell from "./useCopyableCell";
import useOpenLinkCell from "./useOpenLinkCell";
import useOperationCell from "./useOperationCell";

const useTableColumns = () => {
  const { translatePrefix } = usePageConfigProviderContext();
  const { t } = useTranslation(translatePrefix);
  const { hasPermission } = usePermission();
  const { renderOperationCell, renderResubmitPrompter } = useOperationCell();
  const { renderCopyableCell } = useCopyableCell();
  const { renderOpenLinkCell } = useOpenLinkCell();

  const renderDialog = () => {
    return <>{renderResubmitPrompter()}</>;
  };

  const columns = [
    useGenGridCol(TableColumnKey.Operation, t(TableColumnKey.Operation), {
      renderCell: renderOperationCell,
      minWidth: 150,
    }),
    useGenGridCol(TableColumnKey.BatchId, t(TableColumnKey.BatchId)),
    useGenGridCol(TableColumnKey.MerchantName, t(TableColumnKey.MerchantName), {
      minWidth: 150,
    }),
    useGenGridCol(TableColumnKey.ChainName, t(TableColumnKey.ChainName)),
    useGenGridCol(
      TableColumnKey.SmartAccountType,
      t(TableColumnKey.SmartAccountType),
      { minWidth: 120 },
    ),
    useGenGridCol(
      TableColumnKey.FreeBandWidthOnly,
      t(TableColumnKey.FreeBandWidthOnly),
      { minWidth: 120 },
    ),
    useGenGridCol(
      TableColumnKey.SpenderContractVersion,
      t(TableColumnKey.SpenderContractVersion),
      { minWidth: 150 },
    ),
    useGenGridCol(
      TableColumnKey.SpenderAddress,
      t(TableColumnKey.SpenderAddress),
      {
        renderCell: renderCopyableCell("spenderAddress"),
        minWidth: 300,
      },
    ),
    useGenGridCol(
      TableColumnKey.RecipientOwnerAddress,
      t(TableColumnKey.RecipientOwnerAddress),
      {
        renderCell: renderCopyableCell("sweepDestOwnerAddress"),
        minWidth: 300,
      },
    ),
    useGenGridCol(
      TableColumnKey.RecipientAddress,
      t(TableColumnKey.RecipientAddress),
      {
        renderCell: renderCopyableCell("sweepDestAddress"),
        minWidth: 300,
      },
    ),
    useGenGridCol(
      TableColumnKey.RecipientWalletType,
      t(TableColumnKey.RecipientWalletType),
      {
        minWidth: 140,
      },
    ),
    useGenGridCol(TableColumnKey.Status, t(TableColumnKey.Status)),
    useGenGridCol(TableColumnKey.Currency, t(TableColumnKey.Currency)),
    useGenGridCol(
      TableColumnKey.CurrencyOfMinerFee,
      t(TableColumnKey.CurrencyOfMinerFee),
      { minWidth: 130 },
    ),
    useGenGridCol(TableColumnKey.MinerFee, t(TableColumnKey.MinerFee), {
      minWidth: 150,
    }),
    useGenGridCol(TableColumnKey.TotalAmount, t(TableColumnKey.TotalAmount), {
      minWidth: 150,
    }),
    useGenGridCol(TableColumnKey.ActualAmount, t(TableColumnKey.ActualAmount)),
    useGenGridCol(TableColumnKey.TotalCount, t(TableColumnKey.TotalCount)),
    useGenGridCol(TableColumnKey.CreatedBy, t(TableColumnKey.CreatedBy)),

    useGenGridCol(TableColumnKey.CreationTime, t(TableColumnKey.CreationTime), {
      minWidth: 150,
    }),
    useGenGridCol(
      TableColumnKey.LastModifiedTime,
      t(TableColumnKey.LastModifiedTime),
      { minWidth: 150 },
    ),
    useGenGridCol(
      TableColumnKey.ViewOperation,
      t(TableColumnKey.ViewOperation),
      {
        renderCell: renderOpenLinkCell("operationRecords"),
      },
    ),
    useGenGridCol(
      TableColumnKey.ViewOperationTransaction,
      t(TableColumnKey.ViewOperationTransaction),
      {
        renderCell: renderOpenLinkCell("operationTransactionRecords"),
        minWidth: 160,
      },
    ),
    useGenGridCol(
      TableColumnKey.ViewTronRentResourceRecords,
      t(TableColumnKey.ViewTronRentResourceRecords),
      {
        renderCell: renderOpenLinkCell("tronRentResourceRecords"),
        minWidth: 190,
      },
    ),
  ].filter((col) => {
    if (
      col.field === TableColumnKey.Operation &&
      !hasPermission(
        FeatureCodes.assetManagement.ContractSweepRequestRecord.Resubmit,
      )
    ) {
      return false;
    }
    if (
      col.field === TableColumnKey.ViewOperation &&
      !hasPermission(FeatureCodes.assetManagement.OperationRecords.prefix)
    ) {
      return false;
    }
    if (
      col.field === TableColumnKey.ViewOperationTransaction &&
      !hasPermission(
        FeatureCodes.assetManagement.OperationTransactionRecords.prefix,
      )
    ) {
      return false;
    }
    if (
      col.field === TableColumnKey.ViewTronRentResourceRecords &&
      !hasPermission(
        FeatureCodes.assetManagement.TronRentResourceRecords.prefix,
      )
    ) {
      return false;
    }
    return true;
  });
  return { columns, renderDialog };
};

export default useTableColumns;
